import type { AeQuestionDisplayConfig } from '@/types/assessmentEngine';

export type ScaleDisplayStyle = 'numbers' | 'radio' | 'slider' | 'emoji' | 'stars';

export const SCALE_DISPLAY_STYLES: ScaleDisplayStyle[] = [
  'numbers',
  'radio',
  'slider',
  'emoji',
  'stars',
];

export const EMOJI_SCALE = ['😡', '😕', '😐', '🙂', '😍', '🤩', '⭐', '💯'];

/** Default endpoint labels for Likert-style scales (shown when none are configured). */
export const DEFAULT_SCALE_MIN_LABEL = 'Strongly Disagree';
export const DEFAULT_SCALE_MAX_LABEL = 'Strongly Agree';

/** NPS scales use recommendation wording, not Likert agreement. */
export const DEFAULT_NPS_MIN_LABEL = 'Not at all likely';
export const DEFAULT_NPS_MAX_LABEL = 'Extremely likely';

export function resolveScaleEndpointLabels(
  display?: AeQuestionDisplayConfig | null
): { scaleMinLabel: string; scaleMaxLabel: string } {
  return {
    scaleMinLabel: display?.scaleMinLabel?.trim() || DEFAULT_SCALE_MIN_LABEL,
    scaleMaxLabel: display?.scaleMaxLabel?.trim() || DEFAULT_SCALE_MAX_LABEL,
  };
}

/** Clamp scale length to builder range 2–11. */
export function clampScaleLength(n: number): number {
  return Math.min(11, Math.max(2, n));
}

/** Derive scale length from min/max when not stored in display_config. */
export function resolveScaleLength(
  min: number,
  max: number,
  display?: AeQuestionDisplayConfig
): number {
  if (display?.scaleLength) return clampScaleLength(display.scaleLength);
  return clampScaleLength(max - min + 1);
}

/** Integer scale points from min to max inclusive. */
export function scalePoints(min: number, max: number): number[] {
  if (max < min) return [];
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
}

/** NPS is always 0–10. */
export function npsPoints(): number[] {
  return scalePoints(0, 10);
}

export function defaultScaleConfig(): Pick<
  AeQuestionDisplayConfig,
  'scaleLength' | 'scaleDisplayStyle' | 'scaleMinLabel' | 'scaleMaxLabel' | 'reverseScored'
> {
  return {
    // Scale questions render as radio buttons by default; numbers/slider/etc. are
    // opt-in via a Setup scale preset's display style.
    scaleLength: 5,
    scaleDisplayStyle: 'radio',
    scaleMinLabel: DEFAULT_SCALE_MIN_LABEL,
    scaleMaxLabel: DEFAULT_SCALE_MAX_LABEL,
    reverseScored: false,
  };
}

export function defaultNpsConfig(): AeQuestionDisplayConfig {
  return {
    scaleDisplayStyle: 'numbers',
    scaleMinLabel: DEFAULT_NPS_MIN_LABEL,
    scaleMaxLabel: DEFAULT_NPS_MAX_LABEL,
    reverseScored: false,
  };
}

export function defaultMatrixConfig(): AeQuestionDisplayConfig {
  return {
    ...defaultScaleConfig(),
    scaleLength: 5,
  };
}
